import { useState } from 'react';
import { X, Plus, ArrowRight } from '@/lib/icons';
import { useNeighbors } from '@/stores/selectors';
import { useCodexStore } from '@/stores/codexStore';
import { LINK_META } from '@/config/entityMeta';
import { LINK_TYPES, type LinkType } from '@/types/entity';
import { EntityPicker } from './EntityPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFocus } from '@/lib/useFocus';
import { cn } from '@/lib/cn';

export function RelationEditor({ entityId }: { entityId: string }) {
  const neighbors = useNeighbors(entityId);
  const addLink = useCodexStore((s) => s.addLink);
  const removeLink = useCodexStore((s) => s.removeLink);
  const [, setFocus] = useFocus();
  const [pendingType, setPendingType] = useState<LinkType>('related');

  return (
    <div className="space-y-1.5">
      {neighbors.map(({ link, entity }) => {
        const meta = LINK_META[link.type];
        const outgoing = link.from === entityId;
        return (
          <div key={link.id} className="flex items-center gap-2 text-sm">
            <span
              className="flex-none rounded-sm border px-1.5 py-px font-mono text-[10px] uppercase tracking-[0.1em]"
              style={{ color: meta.color, borderColor: meta.color }}
            >
              {meta.label}
            </span>
            {link.directed && (
              <ArrowRight className={cn('size-3 flex-none text-faint', !outgoing && 'rotate-180')} />
            )}
            <button
              type="button"
              onClick={() => setFocus(entity.id)}
              className="truncate text-ink transition hover:text-star"
            >
              {entity.name}
            </button>
            <button
              type="button"
              onClick={() => removeLink(link.id)}
              className="ml-auto flex-none text-fainter transition hover:text-danger"
              aria-label="관계 삭제"
            >
              <X className="size-3.5" />
            </button>
          </div>
        );
      })}

      <div className="flex items-center gap-2 pt-1">
        <Select value={pendingType} onValueChange={(v) => setPendingType(v as LinkType)}>
          <SelectTrigger className="h-7 w-28 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LINK_TYPES.map((t) => (
              <SelectItem key={t} value={t}>
                {LINK_META[t].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <EntityPicker
          exclude={[entityId]}
          onSelect={(toId) => addLink({ from: entityId, to: toId, type: pendingType })}
          trigger={
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-sm border border-dashed border-line-2 px-2 py-1 text-xs text-faint transition hover:border-star hover:text-star"
            >
              <Plus className="size-3" /> 관계
            </button>
          }
        />
      </div>
    </div>
  );
}
